import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Tag, Typography, Space, Button, Tooltip, Popconfirm, Empty, message } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { servicesAPI } from '../../api/client';
import { formatDate, LICENSE_TYPE_LABELS, LICENSE_TYPE_COLORS } from '../Services/constants';
import { fetchAllPages } from '../../utils/fetchAllPages';
import useTableSort from '../../hooks/useTableSort';
import { sortTableRows } from '../../utils/tableRows';

const { Text } = Typography;

export default function LicensesTab({ employeeId }) {
  const queryClient = useQueryClient();
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: 'service_name',
    initialSortDir: 'asc',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['employee-licenses', employeeId],
    queryFn: async () => {
      const items = await fetchAllPages(({ limit, offset }) =>
        servicesAPI.getAssignments({
          employee_id: employeeId,
          active_only: true,
          limit,
          offset,
        }).then(r => r.data)
      );
      return { total: items.length, items };
    },
    enabled:  !!employeeId,
  });

  const rows = useMemo(() => sortTableRows(data?.items || [], {
    sortBy,
    sortDir,
    types: {
      assigned_at: 'date',
    },
  }), [data?.items, sortBy, sortDir]);

  const revokeMutation = useMutation({
    mutationFn: (id) => servicesAPI.revoke(id, {}),
    onSuccess: () => {
      message.success('Доступ отозван');
      queryClient.invalidateQueries(['employee-licenses', employeeId]);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const columns = [
    {
      title: `Сервис${sortIcon('service_name')}`, dataIndex: 'service_name', key: 'service', width: 160,
      onHeaderCell: () => getSortableHeaderProps('service_name'),
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{v}</Text>
          <Tag color={r.category_color} style={{ fontSize: 10 }}>{r.category_name}</Tag>
        </Space>
      ),
    },
    {
      title: `Тип доступа${sortIcon('license_type')}`, dataIndex: 'license_type', key: 'license_type', width: 130,
      onHeaderCell: () => getSortableHeaderProps('license_type'),
      render: (v) => (
        <Tag color={LICENSE_TYPE_COLORS[v]} style={{ fontSize: 10, margin: 0 }}>
          {LICENSE_TYPE_LABELS[v] || v || '—'}
        </Tag>
      ),
    },
    {
      title: `Роль${sortIcon('role')}`, dataIndex: 'role', key: 'role', width: 90,
      onHeaderCell: () => getSortableHeaderProps('role'),
      render: (v) => v ? <Tag style={{ fontSize: 10 }}>{v}</Tag> : '—',
    },
    {
      title: `Выдан${sortIcon('assigned_at')}`, dataIndex: 'assigned_at', key: 'date', width: 100,
      onHeaderCell: () => getSortableHeaderProps('assigned_at'),
      render: (v) => <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text>,
    },
    {
      title: '', key: 'actions', width: 40,
      render: (_, r) => (
        <Popconfirm title="Отозвать доступ?" onConfirm={() => revokeMutation.mutate(r.id)} okText="Да" cancelText="Нет">
          <Tooltip title="Отозвать">
            <Button type="text" size="small" danger icon={<StopOutlined />} />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  if (!isLoading && !rows.length) {
    return <Empty description="Нет активных доступов" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Table
      dataSource={rows} columns={columns}
      rowKey="id" loading={isLoading} size="small"
      pagination={false}
    />
  );
}
